import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import adminService from "../api/adminService";

const AdminPanelPage = () => {
    const navigate = useNavigate();

    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [deletingId, setDeletingId] = useState(null);

    const intelligentModules = [
        ["🚀", "Mission Control", "Central admin command dashboard", "/admin/mission-control"],
        ["🌍", "Global Operations", "Worldwide logistics overview", "/admin/global-operations"],
        ["🛰", "Global Monitoring", "Live operational monitoring", "/admin/global-monitoring"],
        ["📈", "Analytics Center", "Business and logistics analytics", "/admin/analytics-center"],
        ["🤖", "AI Command Center", "AI prediction and decisions", "/admin/ai-command-center"],
        ["🚚", "Predictive Logistics", "Delay and route forecasting", "/admin/predictive-logistics"],
        ["⭐", "Trust Engine v2", "Adaptive trust scoring", "/admin/intelligent-trust-engine"],
        ["🔐", "Security Center", "Security monitoring center", "/admin/security-center"],
        ["🟢", "System Health", "Infrastructure status", "/admin/system-health"],
        ["📡", "IoT Control Center", "Live IoT telemetry", "/admin/iot-control-center"],
    ];

    const adminModules = [
        ["📍", "IoT Map", "Map-based device view", "/admin/iot-map"],
        ["📡", "IoT Devices", "Manage IoT devices", "/admin/iot-devices"],
        ["🛡", "Security Dashboard", "Security events and risks", "/admin/security"],
        ["🔑", "Encryption Keys", "Cryptographic key tools", "/admin/encryption"],
        ["📦", "Package Scan", "Scan and process parcels", "/admin/package-scan"],
        ["🏷", "Cargo Types", "Manage cargo categories", "/admin/cargo-types"],
        ["👥", "Manage Clients", "Client administration", "/admin/clients"],
        ["📊", "Full Admin Dashboard", "Classic admin overview", "/admin"],
        ["🔐", "Adaptive Security", "Adaptive protection logic", "/admin/adaptive-security"],
        ["⭐", "Adaptive Trust Engine", "Trust score algorithm", "/admin/adaptive-trust-engine"],
        ["🧾", "Trust History", "Trust event history", "/admin/trust-history"],
        ["🛰", "Digital Twin", "Shipment digital twin", "/admin/digital-twin"],
        ["⛓", "Smart Contracts", "Blockchain contract logic", "/admin/smart-contracts"],
        ["🏭", "Warehouses", "Warehouse management", "/admin/warehouses"],
        ["🚛", "Drivers", "Driver management", "/admin/drivers"],
        ["🚦", "Dispatch Centers", "Dispatch operations", "/admin/dispatch-centers"],
        ["📈", "Demand Forecast", "Demand prediction", "/admin/demand-forecast"],
        ["💬", "SMS Center", "SMS notification tools", "/admin/sms"],
        ["⛓", "Blockchain Explorer", "Blockchain audit records", "/admin/blockchain-explorer"],
        ["👤", "User Management", "Manage system users", "/admin/users"],
        ["⭐", "Trust Score Engine", "Trust score calculation", "/admin/trust-score"],
        ["🤖", "AI Prediction Center", "AI prediction tools", "/admin/ai-prediction-center"],
        ["🔔", "Notification Center", "System notifications", "/admin/notifications"],
        ["🏢", "Executive Dashboard", "Executive overview", "/admin/executive-dashboard"],
        ["🧩", "Plugin Module Manager", "Manage platform modules", "/admin/platform-modules"],
        ["🏢", "Warehouse Management", "Manage warehouses, zones, shelves, inventory and operations", "/admin/warehouses"],
    ];

    useEffect(() => {
        const token =
            localStorage.getItem("authToken") ||
            sessionStorage.getItem("authToken") ||
            localStorage.getItem("token") ||
            sessionStorage.getItem("token");

        const role =
            localStorage.getItem("role") ||
            sessionStorage.getItem("role");

        if (!token) {
            navigate("/login");
            return;
        }

        if (
            role &&
            role.toLowerCase() !== "admin" &&
            role.toLowerCase() !== "administrator"
        ) {
            setError("Access denied.");
            setLoading(false);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const list = await adminService.getAllAdmins();
                if (!cancelled) {
                    setAdmins(Array.isArray(list) ? list : []);
                }
            } catch (e) {
                if (!cancelled) {
                    const st = e?.response?.status;

                    if (st === 401 || st === 403) {
                        localStorage.clear();
                        sessionStorage.clear();
                        navigate("/login");
                    } else {
                        setError(
                            e?.response?.data?.message ||
                            e?.response?.data?.title ||
                            e?.message ||
                            "Failed to load admins."
                        );
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this admin?")) return;

        setDeletingId(id);
        setError("");
        setSuccess("");

        try {
            await adminService.deleteAdmin(id);
            setAdmins((prev) => prev.filter((a) => (a.id ?? a.adminId) !== id));
            setSuccess("Admin deleted successfully.");
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                e?.response?.data?.title ||
                e?.message ||
                "Failed to delete admin."
            );
        } finally {
            setDeletingId(null);
        }
    };

    if (loading) {
        return <p className="p-6">Loading...</p>;
    }

    return (
        <div className="w-full">
            <div className="mb-8">
                <h1 className="text-3xl font-black text-slate-900">
                    Admin Panel
                </h1>
                <p className="text-slate-600 mt-2">
                    Manage intelligent logistics modules, users, security, blockchain, IoT and AI tools.
                </p>
            </div>

            {error && (
                <div className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            {success && (
                <div className="mb-4 rounded-xl border border-green-300 bg-green-50 p-4 text-green-700">
                    {success}
                </div>
            )}

            <SectionTitle title="Intelligent Platform" subtitle="Advanced AI, IoT, trust and monitoring modules." />

            <ModuleGrid modules={intelligentModules} navigate={navigate} />

            <SectionTitle title="Administration" subtitle="Operational management and platform configuration." />

            <ModuleGrid modules={adminModules} navigate={navigate} />

            <div className="mt-10 bg-white rounded-2xl shadow border border-slate-200 overflow-hidden">
                <div className="p-6 border-b">
                    <h2 className="text-2xl font-bold">System Administrators</h2>
                    <p className="text-slate-500 mt-1">Current administrator accounts.</p>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                            <tr>
                                <th className="text-left px-4 py-3">ID</th>
                                <th className="text-left px-4 py-3">Username</th>
                                <th className="text-left px-4 py-3">Email</th>
                                <th className="text-left px-4 py-3">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {admins.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="text-center py-6 text-slate-500">
                                        No admins available
                                    </td>
                                </tr>
                            ) : (
                                admins.map((admin) => {
                                    const id = admin.id ?? admin.adminId;
                                    const username =
                                        admin.userName ??
                                        admin.username ??
                                        admin.user?.userName ??
                                        "—";
                                    const email =
                                        admin.email ??
                                        admin.user?.email ??
                                        "—";

                                    return (
                                        <tr key={id} className="border-t">
                                            <td className="px-4 py-3">{id}</td>
                                            <td className="px-4 py-3 font-medium">{username}</td>
                                            <td className="px-4 py-3">{email}</td>
                                            <td className="px-4 py-3">
                                                <button
                                                    className={`rounded-lg bg-red-600 px-3 py-2 text-white hover:bg-red-700 ${deletingId === id ? "opacity-50 cursor-wait" : ""
                                                        }`}
                                                    onClick={() => handleDelete(id)}
                                                    disabled={deletingId === id}
                                                >
                                                    {deletingId === id ? "Deleting..." : "Delete"}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const SectionTitle = ({ title, subtitle }) => (
    <div className="mt-8 mb-4">
        <h2 className="text-2xl font-bold text-slate-900">{title}</h2>
        <p className="text-slate-500">{subtitle}</p>
    </div>
);

const ModuleGrid = ({ modules, navigate }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {modules.map(([icon, title, description, path]) => (
            <button
                key={path}
                type="button"
                onClick={() => navigate(path)}
                className="text-left bg-white rounded-2xl border border-slate-200 shadow hover:shadow-xl hover:-translate-y-1 transition p-5"
            >
                <div className="text-4xl mb-4">{icon}</div>
                <h3 className="text-lg font-bold text-slate-900">{title}</h3>
                <p className="text-sm text-slate-500 mt-2">{description}</p>
            </button>
        ))}
    </div>
);

export default AdminPanelPage;